import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["accessibilityLabel", "autoComplete", "autoFocus", "backgroundColorToken", "backgroundColorTokenArgs", "children", "clearIconAccessibilityLabel", "defaultValue", "disabled", "focused", "forceControlled", "hovered", "icon", "id", "inputRef", "keyboardFocused", "keyboardType", "label", "maxLength", "name", "onClick", "onComponentRef", "onBlur", "onChange", "onFocus", "onDisabledClick", "onEnter", "onKeyDown", "onMouseDown", "onMouseOut", "onMouseOver", "onMouseUp", "onPress", "onPressIn", "onPressOut", "onTab", "onTouchEnd", "onTouchStart", "pressed", "readOnly", "showClearIcon", "size", "spellCheck", "tabIndex", "testID", "type", "UNSAFE_inputProps", "value", "width", "withIcon"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import useOnTabAndOnEnter from '@bubble-ui/hook-use-on-tab-and-on-enter';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import useUncontrolledProp from '@bubble-ui/hook-use-uncontrolled-prop';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import Text from '@bubble-ui/primitive-text';
import View from '@bubble-ui/primitive-view';
import TextInput from '@bubble-ui/primitive-text-input';
import { isNative, platformSelect } from '@bubble-ui/util-platform';
import ActionIconSearch from '@bubble-ui/icon-action-search';
import ActionIconClose from '@bubble-ui/icon-action-close';
import TouchableIcon from '@bubble-ui/molecule-touchable-icon';
import { useUniqueFormIdentifier } from '@bubble-ui/hook-use-unique-form-identifier';
import usePropDeprecationWarning from '@bubble-ui/hook-use-prop-deprecation-warning';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { defaultDesignTokens } from './defaultDesignTokens';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { getIds } from './getIds';
var FieldSimple = _ref => {
  var {
      accessibilityLabel,
      autoComplete,
      autoFocus,
      backgroundColorToken,
      backgroundColorTokenArgs,
      children,
      clearIconAccessibilityLabel,
      defaultValue = '',
      disabled,
      focused,
      forceControlled,
      hovered,
      icon: Icon,
      id,
      inputRef,
      keyboardFocused,
      keyboardType,
      label,
      maxLength,
      name,
      onClick,
      onComponentRef,
      onBlur,
      onChange = () => {},
      onFocus,
      onDisabledClick,
      onEnter,
      onKeyDown,
      onMouseDown,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onPress,
      onPressIn,
      onPressOut,
      onTab,
      onTouchEnd,
      onTouchStart,
      pressed,
      readOnly,
      showClearIcon,
      size,
      spellCheck,
      tabIndex,
      testID,
      type,
      UNSAFE_inputProps,
      value,
      width,
      withIcon
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  usePropDeprecationWarning({
    componentName: 'FieldSimple',
    propName: 'forceControlled',
    shouldWarn: forceControlled !== undefined,
    removalDate: 'v6.0.0'
  });
  usePropsSpreadingDeprecationWarning({
    componentName: 'FieldSimple',
    props,
    useInstead: 'UNSAFE_inputProps',
    removalDate: 'v7.0.0'
  });
  var designContext = useDesignContext(defaultDesignTokens);
  var designVersion = useDesignVersion();
  var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombined({
    focused,
    hovered,
    keyboardFocused,
    onBlur,
    onFocus,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed,
    disabled
  });
  var onKeyDownEventHandler = useOnTabAndOnEnter({
    onEnter,
    onKeyDown,
    onTab
  });
  var [fieldValue, setFieldValue] = useUncontrolledProp({
    prop: value,
    defaultProp: defaultValue,
    onChange
  });
  var ids = getIds(id);
  var componentName = useUniqueFormIdentifier({
    name,
    fallbackName: 'FieldSimple'
  });
  var hasValue = fieldValue ? fieldValue.length > 0 : false;
  var isMedium = size === 'medium';
  var canBeCleared = showClearIcon && hasValue && !disabled;
  var {
    clearIconWrapperStyle,
    containerStyle,
    iconColor,
    iconStyle,
    inputStyle,
    labelTextStyle,
    labelViewStyle
    // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
    // Keep passing all props to the style sheet, even if we don't use them ourselves
  } = useStyleSheet(FieldSimple, getDefaultStyleSheet, _objectSpread(_objectSpread({}, props), {}, {
    accessibilityLabel,
    autoComplete,
    autoFocus,
    backgroundColorToken,
    backgroundColorTokenArgs,
    canBeCleared,
    children,
    clearIconAccessibilityLabel,
    defaultValue,
    designVersion,
    designContext,
    disabled: interactiveStateCombined.disabled,
    hasValue,
    focused: interactiveStateCombined.focused,
    forceControlled,
    hovered: interactiveStateCombined.hovered,
    icon: Icon,
    id,
    inputRef,
    isMedium,
    keyboardFocused: interactiveStateCombined.keyboardFocused,
    keyboardType,
    label,
    maxLength,
    name,
    onBlur,
    onClick,
    onChange,
    onComponentRef,
    onDisabledClick,
    onEnter,
    onFocus,
    onKeyDown,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPress,
    onPressIn,
    onPressOut,
    onTab,
    onTouchEnd,
    onTouchStart,
    pressed: interactiveStateCombined.pressed,
    readOnly,
    showClearIcon,
    size,
    spellCheck,
    tabIndex,
    testID,
    type,
    UNSAFE_inputProps,
    value,
    width,
    withIcon
  }));
  return /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.root,
    tag: "label",
    accessibilityState: {
      disabled: interactiveStateCombined.disabled
    },
    htmlFor: (ids === null || ids === void 0 ? void 0 : ids.input) || componentName,
    onClick: e => interactiveStateCombined.disabled && (onDisabledClick === null || onDisabledClick === void 0 ? void 0 : onDisabledClick(e)),
    style: _objectSpread(_objectSpread({}, containerStyle), {}, {
      width: width !== undefined ? width : '100%'
    })
  }, withIcon && /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.icon,
    style: iconStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, Icon !== undefined ? /*#__PURE__*/React.createElement(Icon, iconColor) : /*#__PURE__*/React.createElement(ActionIconSearch, iconColor))), label && label !== '' && /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.label,
    style: labelViewStyle
  }, /*#__PURE__*/React.createElement(Text, {
    style: labelTextStyle,
    numberOfLines: 1
  }, label)), /*#__PURE__*/React.createElement(TextInput, _extends({
    id: (ids === null || ids === void 0 ? void 0 : ids.input) || componentName,
    testID: testID
  }, UNSAFE_inputProps, platformSelect({
    ios: {
      selectionColor: inputStyle.caretColor
    },
    android: {},
    web: {}
  }), {
    accessibilityLabel: accessibilityLabel || label,
    autoComplete: autoComplete,
    autoFocus: autoFocus,
    disabled: interactiveStateCombined.disabled,
    inputRef: inputRef,
    keyboardType: keyboardType,
    maxLength: maxLength,
    onChange: setFieldValue,
    onClick: onClick
  }, onComponentRef ? {
    onComponentRef
  } : {}, {
    onPress: onPress
  }, isNative ? {
    onEnter,
    onKeyDown
  } : {
    onKeyDown: onKeyDownEventHandler
  }, {
    name: componentName,
    readOnly: readOnly,
    spellCheck: spellCheck,
    style: inputStyle,
    tabIndex: tabIndex,
    type: type,
    value: fieldValue || ''
  }, eventHandlers, props), children), showClearIcon && /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.clearIcon,
    style: clearIconWrapperStyle
  }, /*#__PURE__*/React.createElement(TouchableIcon, {
    ariaLabel: clearIconAccessibilityLabel,
    Icon: ActionIconClose,
    onPress: e => setFieldValue('', e)
  })));
};
FieldSimple.displayName = 'FieldSimple';
export default withProvisionalOverrideFromContext(FieldSimple);
//# sourceMappingURL=index.js.map